import React from 'react';
import {
  TableHead,
  TableRow,
  TableCell,
  Box,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
} from '@mui/material';
import {
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  FilterAlt as FilterAltIcon,
} from '@mui/icons-material';

/**
 * ExperimentTableHeader Component
 *
 * Table header with sortable columns and status filter.
 *
 * @param {Object} props
 * @param {string} props.sortBy - Current sort column
 * @param {string} props.sortOrder - Current sort order
 * @param {string} props.statusFilter - Current status filter
 * @param {Function} props.onSort - Handler for sort column click
 * @param {Function} props.onStatusFilterOpen - Handler for opening status filter menu
 * @param {Function} props.onStatusFilterClose - Handler for closing status filter menu
 * @param {Function} props.onStatusFilterSelect - Handler for selecting status filter
 * @param {Object} props.statusFilterAnchor - Anchor element for status filter menu
 */
function ExperimentTableHeader({
  sortBy,
  sortOrder,
  statusFilter,
  onSort,
  onStatusFilterOpen,
  onStatusFilterClose,
  onStatusFilterSelect,
  statusFilterAnchor,
}) {
  const theme = useTheme();

  const renderSortIcon = column => {
    if (sortBy !== column) {
      return null;
    }
    return sortOrder === 'asc' ? (
      <ArrowUpwardIcon
        fontSize="small"
        sx={{ ml: 0.5, verticalAlign: 'middle' }}
      />
    ) : (
      <ArrowDownwardIcon
        fontSize="small"
        sx={{ ml: 0.5, verticalAlign: 'middle' }}
      />
    );
  };

  const isStatusFilterActive = statusFilter !== 'all';

  return (
    <TableHead>
      <TableRow
        sx={{
          backgroundColor: theme.palette.background.default,
          '& .MuiTableCell-head': {
            fontWeight: 600,
            fontSize: '0.875rem',
            color: theme.palette.text.primary,
            borderBottom: `2px solid ${theme.palette.divider}`,
          },
        }}
      >
        <TableCell sx={{ maxWidth: '400px' }}>Name</TableCell>
        <TableCell sx={{ minWidth: '300px' }}>Template</TableCell>
        <TableCell
          sx={{
            cursor: 'pointer',
            userSelect: 'none',
            position: 'relative',
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center">
              Status
            </Box>
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation();
                onStatusFilterOpen(e);
              }}
              sx={{
                ml: 1,
                p: 0.5,
                color: isStatusFilterActive ? 'primary.main' : 'inherit',
              }}
            >
              <FilterAltIcon fontSize="small" />
            </IconButton>
          </Box>
          <Menu
            anchorEl={statusFilterAnchor}
            open={Boolean(statusFilterAnchor)}
            onClose={onStatusFilterClose}
            onClick={e => e.stopPropagation()}
          >
            <MenuItem
              onClick={() => onStatusFilterSelect('all')}
              selected={statusFilter === 'all'}
            >
              All Statuses
            </MenuItem>
            <MenuItem
              onClick={() => onStatusFilterSelect('draft')}
              selected={statusFilter === 'draft'}
            >
              Draft
            </MenuItem>
            <MenuItem
              onClick={() => onStatusFilterSelect('active')}
              selected={statusFilter === 'active'}
            >
              Active
            </MenuItem>
            <MenuItem
              onClick={() => onStatusFilterSelect('paused')}
              selected={statusFilter === 'paused'}
            >
              Paused
            </MenuItem>
            <MenuItem
              onClick={() => onStatusFilterSelect('completed')}
              selected={statusFilter === 'completed'}
            >
              Completed
            </MenuItem>
            <MenuItem
              onClick={() => onStatusFilterSelect('cancelled')}
              selected={statusFilter === 'cancelled'}
            >
              Cancelled
            </MenuItem>
          </Menu>
        </TableCell>
        <TableCell>Treatments</TableCell>
        <TableCell
          sx={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => onSort('created_by')}
        >
          Created By {renderSortIcon('created_by')}
        </TableCell>
        <TableCell
          sx={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => onSort('updated_by')}
        >
          Updated By {renderSortIcon('updated_by')}
        </TableCell>
        <TableCell
          sx={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => onSort('created_at')}
        >
          Created {renderSortIcon('created_at')}
        </TableCell>
        <TableCell
          sx={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => onSort('updated_at')}
        >
          Updated {renderSortIcon('updated_at')}
        </TableCell>
        <TableCell align="center">Actions</TableCell>
      </TableRow>
    </TableHead>
  );
}

export default ExperimentTableHeader;
